import { LocalePrefix } from '@tmh-website/shared/const';
import { DocumentType, graphql } from '@tmh-website/shared/graphql';
import { Product, getMagentoGraphQLClient, getProductPriceObject, getStoreHeaders } from './graphql.service';
import { stubCustomerData } from './stub-data';

const customerQuery = graphql(`
  query customerQuery {
    # Only available when user is logged in
    customerLogged {
      firstname
      lastname
      email
      wishlists {
        id
        items_count
        items_v2 {
          items {
            id
            product {
              sku
            }
          }
        }
      }
    }

    # Custom endpoint: If logged in or in guest mode, will return the current cart id
    getCartId {
      masked_id
    }
  }
`);

const cartQuery = graphql(`
  query cartQuery($id: String!) {
    cart(cart_id: $id) {
      id
      items {
        __typename
        uid
        quantity
        product {
          name
          sku
          url_key
          url_suffix
          small_image {
            url
          }
          price_range {
            minimum_price {
              final_price {
                value
              }
              discount {
                percent_off
              }
              regular_price {
                value
              }
            }
          }
        }
        prices {
          row_total_including_tax {
            currency
            value
          }
          row_total {
            currency
            value
          }
        }
        ... on ConfigurableCartItem {
          configured_variant {
            small_image {
              url
            }
          }
          configurable_options {
            option_label
            value_label
          }
        }
      }
      prices {
        subtotal_including_tax {
          currency
          value
        }
        subtotal_excluding_tax {
          currency
          value
        }
      }
      total_quantity
    }
  }
`);

export interface CustomerCart {
  id: string;
  items: CustomerCartItem[];
  totalQuantity: number;
  subtotalExcludingTax: number;
  subtotalIncludingTax: number;
}

export interface CustomerCartItem {
  uid: string;
  quantity: number;
  product: {
    sku: string;
    url: string;
    name: string;
    small_image: { url: string | null } | null;
    price: Product['price'];
    configured_variant?: {
      small_image: { url: string | null } | null;
    };
  };
  rowPriceExcludingTax: number;
  rowPriceIncludingTax: number;
  /**
   * Options (only non-empty for configurable products).
   */
  options: { label: string; value: string }[];
}

export interface Customer {
  firstName: string;
  lastName: string;
  email: string;
}

export interface Wishlist {
  id: string;
  items: {
    id: string;
    sku: string;
  }[];
}

interface CustomerData {
  customer: Customer | null;
  wishlist: Wishlist | null;
  cartId: string | null;
}

export async function getCustomerData(options: { locale: LocalePrefix; useStubData?: boolean }): Promise<CustomerData> {
  const customerData: CustomerData = {
    customer: null,
    wishlist: null,
    cartId: null,
  };

  if (options?.useStubData) {
    customerData.customer = {
      firstName: stubCustomerData.customer.firstname,
      lastName: stubCustomerData.customer.lastname,
      email: stubCustomerData.customer.email,
    };
    customerData.wishlist = {
      id: stubCustomerData.customer.wishlists[0].id,
      items: stubCustomerData.customer.wishlists[0].items_v2.items.map(item => ({
        id: item.id,
        sku: item.product.sku,
      })),
    };
    customerData.cartId = stubCustomerData.customerCart.id;
  } else {
    const client = getMagentoGraphQLClient(options.locale);
    const { customerLogged: customer, getCartId } = await client.request(
      customerQuery,
      undefined,
      getStoreHeaders(options.locale)
    );

    if (customer) {
      customerData.customer = {
        firstName: customer.firstname ?? '',
        lastName: customer.lastname ?? '',
        email: customer.email ?? '',
      };

      const firstWishlist = customer?.wishlists[0];
      if (firstWishlist) {
        customerData.wishlist = {
          id: firstWishlist.id!,
          items: firstWishlist.items_v2!.items.map(item => ({
            id: item!.id,
            sku: item!.product!.sku ?? '',
          })),
        };
      }
    }

    // Customers are able to be logged out and still have a cart, so we check this separately
    if (getCartId?.masked_id) {
      customerData.cartId = getCartId?.masked_id;
    }
  }

  return customerData;
}

export async function getCartData(options: {
  cartId: string;
  locale: LocalePrefix;
  useStubData?: boolean;
}): Promise<CustomerCart> {
  if (options?.useStubData) {
    return stubCustomerData.customerCart;
  }

  // User has cart, load it and return it
  const client = getMagentoGraphQLClient(options.locale);
  const { cart } = await client.request(cartQuery, { id: options.cartId }, getStoreHeaders(options.locale));
  return {
    id: cart!.id,
    totalQuantity: cart!.total_quantity,
    items: (cart!.items ?? []).map(item => ({
      uid: item!.uid,
      quantity: item!.quantity,
      product: {
        sku: item!.product.sku ?? '',
        url: (item!.product.url_key ?? '') + (item!.product.url_suffix ?? ''),
        name: item!.product!.name ?? '',
        small_image: item!.product.small_image ? { url: item!.product.small_image.url ?? null } : null,
        price: getProductPriceObject(item!.product.price_range),
        ...(item?.__typename === 'ConfigurableCartItem'
          ? {
              configured_variant: {
                small_image: {
                  url: item?.configured_variant?.small_image?.url ?? null,
                },
              },
            }
          : {}),
      },
      rowPriceExcludingTax: item!.prices?.row_total?.value ?? 0,
      rowPriceIncludingTax: item!.prices?.row_total_including_tax?.value ?? 0,
      options:
        item?.__typename === 'ConfigurableCartItem'
          ? (item!.configurable_options ?? []).map(option => ({
              label: option?.option_label ?? '',
              value: option?.value_label ?? '',
            }))
          : [],
    })),
    subtotalExcludingTax: cart!.prices?.subtotal_excluding_tax?.value ?? 0,
    subtotalIncludingTax: cart!.prices?.subtotal_including_tax?.value ?? 0,
  };
}

const removeItemFromCartMutation = graphql(`
  mutation removeItemFromCart($cartId: String!, $cartItemUid: ID!) {
    removeItemFromCart(input: { cart_id: $cartId, cart_item_uid: $cartItemUid }) {
      cart {
        items {
          uid
        }
      }
    }
  }
`);

/**
 * Removes a given item from the cart.
 *
 * @param cartId
 * @param cartItemUid
 */
export async function removeItemFromCart({
  cartId,
  cartItemUid,
  locale,
}: {
  cartId: string;
  cartItemUid: string;
  locale: LocalePrefix;
}): Promise<void> {
  const client = getMagentoGraphQLClient(locale);
  await client.request(removeItemFromCartMutation, { cartId, cartItemUid }, getStoreHeaders(locale));
}

const updateCartItemQuantityMutation = graphql(`
  mutation updateCartItems($cartId: String!, $cartItemUid: ID!, $quantity: Float!) {
    updateCartItems(input: { cart_id: $cartId, cart_items: [{ cart_item_uid: $cartItemUid, quantity: $quantity }] }) {
      cart {
        id
      }
    }
  }
`);

/**
 * Removes a given item from the cart.
 *
 * @param cartId
 * @param cartItemUid
 */
export async function updateCartItemQuantity({
  cartId,
  cartItemUid,
  quantity,
  locale,
}: {
  cartId: string;
  cartItemUid: string;
  quantity: number;
  locale: LocalePrefix;
}): Promise<void> {
  const client = getMagentoGraphQLClient(locale);
  await client.request(updateCartItemQuantityMutation, { cartId, cartItemUid, quantity }, getStoreHeaders(locale));
}
